/**
 * BridgeStatusPage — the patient's "portal": a tracker for their form.
 *
 * Landed on by status-purpose magic links (?dest=status) and by any link
 * whose assignment is already submitted. Shows:
 *   - the DocuSign-style progress rail (sent → signed → faxed to insurer →
 *     decision), driven by the assignment status + journey milestones the
 *     Mac mirrors to the bridge on submit/decision
 *   - the journey history with dates
 *   - the "received a letter? upload it here" card (InsurerLetterUpload)
 *
 * A request the patient closed by choosing "I have decided not to have this
 * form filled out" (ruling 2026-10-14; wording G20, 2026-10-15) shows no
 * rail, no "Pay the fee" and no letter upload: the patient sees the declined
 * card (their payment row reads `declined_old_way`, or `patient-status` says
 * `closed`): nothing charged, the office told; every other signer (the plan member) sees only
 * "This request was closed. Nothing more is needed from you." from
 * `patient-status`'s neutral `closed`, never why.
 *
 * Auth: the bridge session established by the magic link (X-Patient-Token),
 * AND the identity challenge — since 2026-09-05 (case imp_bad9596e) this page
 * asks for the date of birth before it fetches anything.
 *
 * That is a deliberate behaviour change: status and check-in links never used
 * to ask. Two reasons they now do. `patient-status` returns the form title,
 * which names the requested medication — a clinical fact about a person,
 * handed out on link possession alone. And the check-in card posts to
 * `letter-upload`, an endpoint that ACCEPTS a submission. Both are now
 * verified-session-only on the bridge, so a status link without a challenge
 * would simply 401; the choice was to gate them properly, not to carve them
 * out. Cost: one date of birth on the check-in nudge.
 */

import React, { useEffect, useState } from "react";
import { fetchPaymentStatus, getBridgeSession } from "../../lib/bridgeClient";
import { canPayFromLink, feeRowLabel, paymentRoleAllowed } from "../../lib/paymentDisplay";
import FeeStage from "./FeeStage";
import { FeeDeclinedNotice, RequestClosedNotice } from "./FeeDeclined";
import type { PatientPaymentStatus } from "../../types/shared";
import DobChallenge from "./DobChallenge";
import InsurerLetterUpload from "./InsurerLetterUpload";
import ParseoLogo from "../../shared/ui/ParseoLogo";

const BRIDGE_BASE_URL = (import.meta.env.VITE_BRIDGE_URL || "").replace(/\/+$/, "");

const C = {
  navy: "#182a33",
  muted: "#5b6e7c",
  sage: "#6b9c8e",
  sageDark: "#46685d",
  cream: "#f5f0eb",
  line: "#e4dccd",
  faint: "#8a96a3",
};

function fmtDate(iso?: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d as unknown as number)) return "";
  return d.toLocaleDateString("en-CA", { month: "short", day: "numeric" });
}

export default function BridgeStatusPage() {
  const session = getBridgeSession();
  const [status, setStatus] = useState<any>(null);
  const [error, setError] = useState("");
  // Gate the page on the identity challenge when the bridge says this session
  // needs one. `identityRequired` comes from redeem; a session stored by an
  // older build lacks the key, and `!== false` keeps those challenged rather
  // than silently ungated.
  const [verified, setVerified] = useState(
    () => getBridgeSession()?.identityRequired === false
  );
  // Check-in landing (10-day "has a letter arrived?" nudge): lead with a
  // direct Yes/No question; the camera/upload card only appears on Yes.
  const isCheckin = new URLSearchParams(window.location.search).get("checkin") === "1";
  const [checkinAnswer, setCheckinAnswer] = useState<"yes" | "no" | null>(null); // null | "yes" | "no"
  // The form's payment row, for the rail's Fee line and the "Pay the fee"
  // button. Asked for only by the roles the payment endpoints serve (legacy,
  // patient, or none) — never a plan member or prescriber: payment and waiver
  // details are the patient's alone, and the bridge refuses those roles too.
  // No row → no Fee line.
  const [payment, setPayment] = useState<PatientPaymentStatus | null>(null);
  const [paying, setPaying] = useState(false);
  const paymentsAllowed = paymentRoleAllowed(session?.role);

  useEffect(() => {
    if (!session?.sessionToken || !session?.assignmentId) return;
    if (!verified) return;   // the endpoint would 401 anyway
    let cancelled = false;
    fetch(
      `${BRIDGE_BASE_URL}/api/bridge/assignments/${session.assignmentId}/patient-status`,
      { headers: { "X-Patient-Token": session.sessionToken } }
    )
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`status ${r.status}`))))
      .then((d) => { if (!cancelled) setStatus(d); })
      .catch(() => { if (!cancelled) setError("Couldn't load the latest status. Try the link again in a moment."); });
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [verified]);

  useEffect(() => {
    if (!session?.sessionToken || !session?.assignmentId) return;
    if (!verified || !paymentsAllowed) return;
    let cancelled = false;
    fetchPaymentStatus().then((r) => {
      if (!cancelled && r.kind === "ok") setPayment(r.payment);
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [verified, paymentsAllowed]);

  // Prove who you are before any of this page's content is fetched.
  if (session?.sessionToken && !verified) {
    return (
      <Centered>
        <ParseoLogo size={48} />
        <DobChallenge
          identityFactors={(session?.identityFactors as string[] | undefined) || ["dob"]}
          onPass={() => setVerified(true)}
        />
      </Centered>
    );
  }

  if (!session?.sessionToken) {
    return (
      <Centered>
        <ParseoLogo size={48} />
        <h2 style={{ color: C.navy, margin: "14px 0 6px" }}>Session expired</h2>
        <p style={{ color: C.muted, maxWidth: 420, lineHeight: 1.5 }}>
          Tap the link in your most recent text or email from Parseo Health to
          get back in.
        </p>
      </Centered>
    );
  }

  const journey: any[] = status?.journey || [];
  const hasStage = (s: string) => journey.some((j) => j.stage === s);
  const stageAt = (s: string) => fmtDate((journey.find((j) => j.stage === s) || {}).at);
  const signed = ["signed", "acked"].includes(status?.status) || !!status?.signed_at;
  const faxed = hasStage("submitted_to_insurer");
  const decisionEvt = journey.find((j) =>
    // plan_exclusion: the plan does not include the medication — its own
    // outcome, not a denial (case imp_851c22ae). The label says it in words.
    ["approved", "denied", "plan_exclusion", "requires_info", "determination_received"].includes(j.stage)
  );

  // Closed by the patient (ruling 2026-10-14). The patient reads it from
  // their own payment row, or from `patient-status` when that row cannot be
  // read; any other signer only ever from `patient-status`, which never says
  // why.
  const declined = paymentsAllowed && (payment?.status === "declined_old_way" || status?.closed === true);
  const closedForSigner = !paymentsAllowed && status?.closed === true;
  const ended = declined || closedForSigner;

  const feeRow = paymentsAllowed ? feeRowLabel(payment) : null;
  const steps: Array<{ label: string; done: boolean; sub?: string; active?: boolean }> = [
    { label: "Form sent to you", done: true },
    // The patient of a fee-only request signs nothing; the plan member signs
    // the form (G23(f), patient-status `nothing_to_sign`). DRAFT.
    { label: status?.nothing_to_sign ? "Form signed" : "You signed", done: signed,
      sub: fmtDate(status?.signed_at) },
    ...(feeRow ? [{ label: `Fee: ${feeRow.label}`, done: feeRow.done }] : []),
    { label: "Faxed to your insurer", done: faxed, sub: stageAt("submitted_to_insurer") },
    { label: "Insurer reviewing", done: !!decisionEvt || false, active: faxed && !decisionEvt },
    {
      label: decisionEvt ? decisionEvt.label : "Decision",
      done: !!decisionEvt,
      sub: decisionEvt ? fmtDate(decisionEvt.at) : "",
    },
  ];

  return (
    <div style={{ minHeight: "100vh", backgroundColor: C.cream, padding: "32px 16px" }}>
      <div style={{
        maxWidth: 560, margin: "0 auto", backgroundColor: "#fff",
        borderRadius: 14, padding: "26px 22px", border: `1px solid ${C.line}`,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
          <ParseoLogo size={34} />
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: C.navy }}>
              Your request status
            </div>
            {status?.form_title && (
              <div style={{ fontSize: 12, color: C.muted }}>{status.form_title}</div>
            )}
          </div>
        </div>

        {error && (
          <div style={{ margin: "12px 0", fontSize: 13, color: "#c25450" }}>{error}</div>
        )}

        {declined ? (
          <div style={{ margin: "18px 0 6px", padding: "14px 16px", borderRadius: 10, background: C.cream, border: `1px solid ${C.line}` }}>
            <FeeDeclinedNotice payment={payment} compact />
          </div>
        ) : null}
        {closedForSigner ? (
          <div style={{ margin: "18px 0 6px", padding: "14px 16px", borderRadius: 10, background: C.cream, border: `1px solid ${C.line}` }}>
            <RequestClosedNotice compact />
          </div>
        ) : null}

        {/* Check-in question — shown first on 10-day check-in links */}
        {!ended && isCheckin && checkinAnswer !== "yes" && (
          <div style={{
            margin: "16px 0 4px", padding: "14px 16px", borderRadius: 10,
            background: C.cream, border: `1px solid ${C.line}`,
          }}>
            {checkinAnswer === "no" ? (
              <p style={{ fontSize: 13.5, color: C.sageDark, margin: 0, lineHeight: 1.5 }}>
                Thanks for letting us know — no action needed. We'll check in
                again in about 10 days if we haven't heard from your insurer.
              </p>
            ) : (
              <>
                <div style={{ fontSize: 14.5, fontWeight: 700, color: C.navy, marginBottom: 6 }}>
                  Have you received a response from your insurer?
                </div>
                <p style={{ fontSize: 12.5, color: C.muted, lineHeight: 1.5, margin: "0 0 12px" }}>
                  Insurers often mail you their decision before your doctor's
                  office hears anything. A quick answer keeps your request moving.
                </p>
                <div style={{ display: "flex", gap: 10 }}>
                  <button
                    onClick={() => setCheckinAnswer("yes")}
                    style={{
                      flex: 1, padding: "11px 0", borderRadius: 8, border: "none",
                      background: "#1b5e6e", color: "#fff", fontSize: 14,
                      fontWeight: 600, cursor: "pointer",
                    }}
                  >
                    Yes, I got something
                  </button>
                  <button
                    onClick={() => setCheckinAnswer("no")}
                    style={{
                      flex: 1, padding: "11px 0", borderRadius: 8,
                      border: `1px solid ${C.line}`, background: "#fff",
                      color: C.navy, fontSize: 14, fontWeight: 600, cursor: "pointer",
                    }}
                  >
                    No, nothing yet
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Progress rail */}
        {!ended && (
        <div style={{ margin: "22px 0 8px" }}>
          {steps.map((s, i) => (
            <div key={i} style={{ display: "flex", gap: 12, minHeight: 44 }}>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                <div style={{
                  width: 26, height: 26, borderRadius: "50%", flexShrink: 0,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 13, fontWeight: 700,
                  background: s.done ? C.sage : s.active ? "#1b5e6e" : C.line,
                  color: s.done || s.active ? "#fff" : C.faint,
                }}>
                  {s.done ? "✓" : i + 1}
                </div>
                {i < steps.length - 1 && (
                  <div style={{ width: 2, flex: 1, background: steps[i + 1].done ? C.sage : C.line }} />
                )}
              </div>
              <div style={{ paddingBottom: 14 }}>
                <div style={{
                  fontSize: 14, fontWeight: 600, lineHeight: "26px",
                  color: s.done ? C.sageDark : s.active ? "#1b5e6e" : C.faint,
                }}>
                  {s.label}
                  {s.active ? "…" : ""}
                </div>
                {s.sub && <div style={{ fontSize: 11, color: C.faint }}>{s.sub}</div>}
              </div>
            </div>
          ))}
        </div>
        )}

        {/* Pay later: a deferred row saves a card, a failed charge pays now
            (payment-mode Checkout). Same new-tab flow as the fee step. */}
        {!ended && paymentsAllowed && canPayFromLink(payment) && payment ? (
          paying ? (
            <div style={{ margin: "4px 0 18px" }}>
              <FeeStage
                mode="pay"
                payment={payment}
                onBack={() => setPaying(false)}
                // G23(c): a card declined right before the send can still be
                // declined; the page then shows its closing notice.
                onDecline={() => {
                  setPayment({ ...payment, status: "declined_old_way", declinable: false });
                  setPaying(false);
                }}
                onContinue={(_outcome, _message, latest) => {
                  if (latest) setPayment(latest);
                  setPaying(false);
                }}
              />
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setPaying(true)}
              style={{
                margin: "4px 0 16px", padding: "11px 18px", borderRadius: 8, border: "none",
                background: "#1b5e6e", color: "#fff", fontSize: 14, fontWeight: 600, cursor: "pointer",
              }}
            >
              Pay the fee
            </button>
          )
        ) : null}

        {!ended && (
        <p style={{ fontSize: 13, color: C.muted, lineHeight: 1.5, margin: "6px 0 18px" }}>
          We'll text and email you when anything changes — no need to check back
          on your own.
          {faxed && !decisionEvt && (
            <>
              {" "}Heads-up: your insurer may mail their decision to you before
              our office hears anything — when a letter arrives, photograph it
              below and it reaches your doctor right away.
            </>
          )}
        </p>
        )}

        {/* Letter intake — on a check-in landing it only appears after "Yes" */}
        {!ended && (!isCheckin || checkinAnswer === "yes") && (
          <div style={{ borderTop: `1px solid ${C.line}`, paddingTop: 16 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: C.navy, marginBottom: 4 }}>
              {isCheckin ? "Great — snap a photo of what you received"
                         : "Did your insurer send you a letter?"}
            </div>
            <p style={{ fontSize: 12.5, color: C.muted, lineHeight: 1.5, margin: "0 0 10px" }}>
              {isCheckin
                ? "Take a photo or upload the letter and it goes straight to your doctor's office — it's read automatically, no phone call needed."
                : "Insurers often mail their decision to you directly. Photograph or upload it here and it goes straight to your doctor's office — securely, without a phone call."}
            </p>
            <InsurerLetterUpload assignmentId={session.assignmentId} />
          </div>
        )}
      </div>
    </div>
  );
}

function Centered({ children }: { children?: React.ReactNode }) {
  return (
    <div style={{
      minHeight: "100vh", display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center",
      backgroundColor: C.cream, padding: 24, textAlign: "center",
    }}>
      {children}
    </div>
  );
}
